// EmailDraftButton — a plain button to draft a client email into the IM's
// Outlook mailbox.
//
// Clicking it calls `onDraft()` (the parent owns the actual draft call + the
// post-draft "open it" behaviour). The button is enabled whenever Microsoft
// Entra is configured (matching the always-Connected Settings card); when it is
// not, the button is disabled with an explanatory hint.

import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOutlookStatus } from '@/lib/fact-find/hooks';

interface EmailDraftButtonProps {
    /** Called on click; the parent performs the Outlook draft call. */
    onDraft: () => void | Promise<void>;
    /** Spinner + disabled while a draft is in flight. */
    busy?: boolean;
    disabled?: boolean;
    size?: 'sm' | 'md';
    /** Trigger label. */
    label?: string;
}

export default function EmailDraftButton({
    onDraft,
    busy = false,
    disabled = false,
    size = 'sm',
    label = 'Draft email',
}: EmailDraftButtonProps) {
    const { data: outlookStatus } = useOutlookStatus();

    // Outlook tracks the Settings card: visually Connected whenever Entra is
    // configured. A missing token surfaces as a clear error on draft.
    const unavailable = outlookStatus?.configured === false;
    const hint = unavailable
        ? 'Outlook unavailable — Microsoft Entra is not configured'
        : undefined;

    return (
        <button
            type="button"
            disabled={disabled || busy || unavailable}
            onClick={() => void onDraft()}
            title={hint}
            className={cn(
                'inline-flex items-center rounded-button font-medium',
                'bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text-primary))]',
                'border border-[rgb(var(--color-divider))]',
                'hover:bg-[rgb(var(--color-surface-secondary))] hover:border-[rgb(var(--color-text-primary)/0.2)]',
                'transition-colors duration-card-hover',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.4)]',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                size === 'sm'
                    ? 'h-7 px-2.5 gap-1.5 text-annotation'
                    : 'h-9 px-3.5 gap-2 text-body-medium',
            )}
        >
            {busy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
                <img src="/logos/outlook.svg" alt="" aria-hidden className="h-3.5 w-3.5" />
            )}
            {label}
        </button>
    );
}
